import {ApolloClient, BatchHttpLink, InMemoryCache, withClientState, createNetworkStatusNotifier, ApolloLink, WebSocketLink, getMainDefinition, split, HttpLink} from '@apollo'
import {lockr, _} from '@api'
import {configs} from '@stores/config'
import {synchronizeStores} from '@stores/SynchronizeStores'
import {ui} from '@stores/ui'
import {defaultDataIdFromObject} from 'apollo-cache-inmemory'
import {action, autorun, observable} from 'mobx'
import {localCache} from './defaults'
import {setContext} from 'apollo-link-context'
import {networkStatusNotifierLink, utilityInterceptor, authLink} from './'
import { authStore } from '@utils'

export class ApolloStore {
  @observable.ref client: ApolloClient<{}>

  constructor() {
    autorun(() => {
      configs.config && this.initializeApolloClient()
    }, {name: `Initialize apollo client from config change`})
  }

  initializeApolloClient = () => {
    const {config} = configs

    if (TEST) return

    // Set up apollo-link-state
    const cache = new InMemoryCache({
      dataIdFromObject: (object: any) => {
        if (!object.id && object.node) { return `${object.node.__typename ? `${object.node.__typename}:` : ''}${object.node.id}` }
        return defaultDataIdFromObject(object)
      },
      addTypename: false
    })

    const stateLink = withClientState({
      resolvers: {},
      defaults: {...localCache.typeDefault},
      cache

    })

    const batchLinkWithAuthToken = new BatchHttpLink({
      uri: config.apiGatewayEndpoint,
      batchMax: ui.settings.batch.max,
      batchInterval: ui.settings.batch.ms,
      batchKey: (operation): string => {
        return operation?.getContext()?.batchKey || 'default'
      }
    })
    const httpLinkWithAuthToken = new HttpLink({uri: config.apiGatewayEndpoint})

    let graphQLSubscriptionUrl = config.wsApiGatewayEndpoint
    if (!_.includes(graphQLSubscriptionUrl, 'ws://')) {
      graphQLSubscriptionUrl = ((window.location.protocol === 'https:') ? 'wss://' : 'ws://') + window.location.host + graphQLSubscriptionUrl
    }

    const wsLink = graphQLSubscriptionUrl && new WebSocketLink({
      uri: graphQLSubscriptionUrl,
      options: {
        lazy: true,
        reconnect: window.location.hostname != 'localhost',
        connectionParams: () => {
          return {Authorization: lockr.get('access_token') ? lockr.get('access_token') : authStore.accessToken}
        },
        connectionCallback: error => {
          //console.error(error)
          //debugger
        }
      }
    })

    const dedupeLink = setContext((op, prev) => {
      const {headers} = prev

      return {
        ...prev,
        headers: {
          ...headers,
          'X-GraphQL-Deduplicate': 'true'
        }
      }
    })

    const debugHeaderLink = setContext((op, prev) => {
      if (!DEBUG) return prev

      const {headers, alias} = prev

      if (alias) {
        return {
          ...prev,
          headers: {
            ...headers,
            'x-debug-alias': alias
          }
        }
      }
      return prev
    })

    const linkToUse = split(
      operation => {
        // console.log(operation)
        const context = operation.getContext()
        // Return true to NOT be batched
        return context.batch === false ? true : operation.query.definitions[0]['operation'] == 'mutation'
      },
      httpLinkWithAuthToken,
      batchLinkWithAuthToken
    )

    const httpLink = ApolloLink.from([
      dedupeLink,
      networkStatusNotifierLink,
      utilityInterceptor.trimMiddleware,
      authLink, /* logUtils.loggingMiddleware, logUtils.loggingAfterware, */,
      debugHeaderLink,
      linkToUse
    ])
    const link = split(
      ({query}) => {
        const {kind, operation} = getMainDefinition(query as any) as any
        return wsLink && kind === 'OperationDefinition' && operation === 'subscription'
      },
      wsLink,
      httpLink
    )
    this.client = new ApolloClient({
      link: ApolloLink.from([stateLink, link]),
      cache,
      resolvers: {},
      defaultOptions: {
        watchQuery: {
          fetchPolicy: 'no-cache',
          errorPolicy: 'ignore'
        },
        query: {
          fetchPolicy: 'no-cache',
          errorPolicy: 'all'
        }
      }
    })
  }
}

export const apolloStore = new ApolloStore()



// WEBPACK FOOTER //
// ./src/stores/apollo/ApolloStore.ts